import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, timer, throwError, from, of } from 'rxjs';
import { switchMap, takeWhile, last, timeout, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  SignRseAppearance,
  SignRseCreateBase64Request,
  SignRseProcessResponse,
  SignRseSignatureType
} from '../models/sign-rse-api.model';
import { parseContentDispositionFileName } from '../core/utils/sign-rse.utils';
import { CipherService } from '../core/services/cipher.service';

export interface SignRseSignedDownload {
  blob: Blob;
  fileName: string | null;
  process: SignRseProcessResponse;
}

/** Campos de create multipart (BFF / Sign_RSE). Apariencia PAdES va aplanada. */
export interface SignRseCreateMultipartParams {
  document: File | Blob;
  /** Nombre del part `document` si `document` es Blob sin name. */
  fileName?: string;
  signatureType: SignRseSignatureType;
  origin: string;
  /** Si se omite, se usa environment.originChannel. */
  originChannel?: string;
  identificationNumber?: string;
  identificationType?: string;
  correlationId?: string;
  outputExtension?: string;
  consumerCallbackUrl?: string;
  appearance?: SignRseAppearance;
}

@Injectable({
  providedIn: 'root'
})
export class RseSignService {
  private readonly processesBaseUrl =
    (environment as { signRseProcessesBaseUrl?: string }).signRseProcessesBaseUrl
    ?? `${environment.apiBaseUrl}/api/sign/rse/processes`;

  private readonly pollIntervalMs =
    (environment as { signRsePollIntervalMs?: number }).signRsePollIntervalMs ?? 2000;

  private readonly pollTimeoutMs =
    (environment as { signRsePollTimeoutMs?: number }).signRsePollTimeoutMs ?? 300000;

  constructor(
    private http: HttpClient,
    private cipher: CipherService
  ) {}

  /**
   * Crear proceso RSE (JSON / Base64) — POST /api/sign/rse/processes/base64 → 202
   */
  createFromBase64(request: SignRseCreateBase64Request): Observable<SignRseProcessResponse> {
    return this.postEncrypted<SignRseProcessResponse>(
      `${this.processesBaseUrl}/base64`,
      this.toCreateBody(request)
    );
  }

  /**
   * Crear proceso RSE (multipart) — POST /api/sign/rse/processes/multipart → 202
   * No fijar Content-Type: el browser añade el boundary.
   */
  createFromMultipart(formData: FormData): Observable<SignRseProcessResponse> {
    return this.http.post<SignRseProcessResponse>(`${this.processesBaseUrl}/multipart`, formData);
  }

  /**
   * Arma FormData según contrato BFF: part `document` + params planos.
   * Cifra identificationNumber cuando cipher está activo.
   */
  async buildCreateMultipartForm(params: SignRseCreateMultipartParams): Promise<FormData> {
    const form = new FormData();
    const name =
      params.fileName?.trim() ||
      (params.document instanceof File && params.document.name
        ? params.document.name
        : 'document');
    form.append('document', params.document, name);
    form.append('signatureType', params.signatureType);
    form.append('origin', params.origin);
    form.append('originChannel', this.resolveOriginChannel(params.originChannel));

    if (params.identificationNumber?.trim()) {
      const rawId = params.identificationNumber.trim();
      if (this.cipher.isEncryptionEnabled) {
        form.append('identificationNumber', await this.cipher.encrypt(rawId));
        form.append('sensitiveEncrypted', 'true');
      } else {
        form.append('identificationNumber', rawId);
      }
    }
    if (params.identificationType?.trim()) {
      form.append('identificationType', params.identificationType.trim());
    }
    if (params.correlationId?.trim()) {
      form.append('correlationId', params.correlationId.trim());
    }
    if (params.outputExtension?.trim()) {
      form.append('outputExtension', params.outputExtension.trim());
    }
    if (params.consumerCallbackUrl?.trim()) {
      form.append('consumerCallbackUrl', params.consumerCallbackUrl.trim());
    }

    const appearance = params.appearance;
    if (appearance) {
      if (appearance.pageNumber != null) {
        form.append('pageNumber', String(appearance.pageNumber));
      }
      if (appearance.x != null) {
        form.append('x', String(appearance.x));
      }
      if (appearance.y != null) {
        form.append('y', String(appearance.y));
      }
      if (appearance.width != null) {
        form.append('width', String(appearance.width));
      }
      if (appearance.height != null) {
        form.append('height', String(appearance.height));
      }
      if (appearance.imageBase64?.trim()) {
        form.append('imageBase64', appearance.imageBase64.trim());
      } else if (appearance.useCerticamaraStamp != null) {
        form.append('useCerticamaraStamp', String(appearance.useCerticamaraStamp));
      }
    }

    return form;
  }

  getProcess(processId: string): Observable<SignRseProcessResponse> {
    return this.http.get<SignRseProcessResponse>(`${this.processesBaseUrl}/${processId}`, {
      params: { originChannel: this.resolveOriginChannel() }
    });
  }

  getResult(processId: string): Observable<{ blob: Blob; fileName: string | null }> {
    return this.http.get(`${this.processesBaseUrl}/${processId}/result`, {
      params: { originChannel: this.resolveOriginChannel() },
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) => ({
        blob: response.body ?? new Blob(),
        fileName: parseContentDispositionFileName(
          response.headers.get('Content-Disposition')
        )
      }))
    );
  }

  cancel(processId: string): Observable<void> {
    return this.http.post<void>(`${this.processesBaseUrl}/${processId}/cancel`, null);
  }

  /**
   * Polling hasta estado terminal (COMPLETED | FAILED | CANCELED).
   */
  pollUntilTerminal(processId: string): Observable<SignRseProcessResponse> {
    const terminal = new Set(['COMPLETED', 'FAILED', 'CANCELED']);
    return timer(0, this.pollIntervalMs).pipe(
      switchMap(() => this.getProcess(processId)),
      takeWhile((p) => !terminal.has(String(p.status).toUpperCase()), true),
      last(),
      timeout({
        each: this.pollTimeoutMs,
        with: () =>
          throwError(
            () => new Error('Tiempo de espera agotado esperando el código de verificación.')
          )
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Poll + descarga del documento firmado cuando status = COMPLETED.
   */
  waitAndDownload(processId: string): Observable<SignRseSignedDownload> {
    return this.pollUntilTerminal(processId).pipe(
      switchMap((process) => {
        const status = String(process.status).toUpperCase();
        if (status !== 'COMPLETED') {
          const msg =
            process.errorMessage ||
            `El proceso de firma terminó en estado ${process.status}`;
          return throwError(() => new Error(msg));
        }
        return this.getResult(processId).pipe(
          map(({ blob, fileName }) => ({ process, blob, fileName }))
        );
      })
    );
  }

  /** Omite opcionales vacíos/null para alinear con el DTO del BFF / Sign_RSE. */
  private toCreateBody(request: SignRseCreateBase64Request): Record<string, unknown> {
    const body: Record<string, unknown> = {
      signatureType: request.signatureType,
      documentBase64: request.documentBase64,
      origin: request.origin,
      originChannel: this.resolveOriginChannel(request.originChannel)
    };
    if (request.identificationNumber?.trim()) {
      body['identificationNumber'] = request.identificationNumber.trim();
    }
    if (request.identificationType?.trim()) {
      body['identificationType'] = request.identificationType.trim();
    }
    if (request.correlationId?.trim()) {
      body['correlationId'] = request.correlationId.trim();
    }
    if (request.fileName?.trim()) {
      body['fileName'] = request.fileName.trim();
    }
    if (request.contentType?.trim()) {
      body['contentType'] = request.contentType.trim();
    }
    if (request.outputExtension?.trim()) {
      body['outputExtension'] = request.outputExtension.trim();
    }
    if (request.consumerCallbackUrl?.trim()) {
      body['consumerCallbackUrl'] = request.consumerCallbackUrl.trim();
    }
    if (request.appearance) {
      body['appearance'] = this.toAppearanceBody(request.appearance);
    }
    return body;
  }

  private toAppearanceBody(appearance: NonNullable<SignRseCreateBase64Request['appearance']>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (appearance.pageNumber != null) out['pageNumber'] = appearance.pageNumber;
    if (appearance.x != null) out['x'] = appearance.x;
    if (appearance.y != null) out['y'] = appearance.y;
    if (appearance.width != null) out['width'] = appearance.width;
    if (appearance.height != null) out['height'] = appearance.height;
    if (appearance.imageBase64?.trim()) {
      out['imageBase64'] = appearance.imageBase64.trim();
    } else if (appearance.useCerticamaraStamp != null) {
      out['useCerticamaraStamp'] = appearance.useCerticamaraStamp;
    }
    return out;
  }

  private resolveOriginChannel(value?: string | null): string {
    const fromEnv = (environment as { originChannel?: string }).originChannel;
    return (value?.trim() || fromEnv?.trim() || 'CERTITOOL WEB');
  }

  private postEncrypted<T>(url: string, body: unknown): Observable<T> {
    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<T>(url, body);
    }

    return from(this.cipher.encrypt(JSON.stringify(body))).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | T>(url, { data: encrypted })
      ),
      switchMap((response) => this.unwrapCipherResponse<T>(response))
    );
  }

  private unwrapCipherResponse<T>(response: { data?: string } | T): Observable<T> {
    const wrapped = response as { data?: unknown };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => JSON.parse(plain) as T)
      );
    }
    return of(response as T);
  }
}
